<template>
  <Card class="px-2" pt:body:class="p-3!" pt:content:class="p-0!">
    <template #header>
      <div v-if="messages.length" class="flex flex-col gap-2">
        <Message severity="error" :closeable="false">
          <div class="flex flex-col gap-1">
            <p v-for="message in messages">{{ message }}</p>
          </div>
        </Message>
      </div>
    </template>
    <template #content>
      <form @submit="onSubmit">
        <div>
          <div class="flex flex-row w-full gap-2">
            <StackedLabelInput name="firstName" label="First Name" classes="required" />
            <StackedLabelInput name="lastName" label="Last Name" classes="required" />
          </div>
          <div class="flex flex-row w-full gap-2">
            <StackedLabelInput name="userName" label="Username" classes="required" />
            <StackedLabelInput name="email" label="E-mail" classes="required" />
            <StackedLabelDropdown
              v-if="!user || user.user"
              name="userGroup"
              label="User Group"
              :options="userGroups"
              optionsLabel="name"
              classes="required"
            />
          </div>
          <div v-if="!user || user.user">
            <Fieldset legend="Permissions" class="my-3 w-full" pt:legend:class="px-2 font-bold" pt:content:class="py-0!">
              <div class="flex flex-col -mt-4 mb-2">
                <label class="font-bold">Security Group Settings:</label>
                <div class="flex gap-2">
                  <Select
                    v-model="securityGroup"
                    :options="securityGroups"
                    optionLabel="name"
                    :show-clear="true"
                    scrollHeight="25rem"
                    class="h-10 grow"
                  ></Select>
                  <Button
                    label="Save"
                    icon="pi pi-save"
                    iconPos="right"
                    class="h-10"
                    :disabled="!securityGroup"
                    @click="saveSecurityGroupPermissions"
                  ></Button>
                  <Button
                    label="Delete"
                    icon="pi pi-times"
                    iconPos="right"
                    class="h-10"
                    severity="danger"
                    :disabled="!securityGroup"
                    @click="deleteSecurityGroupPermissions"
                  ></Button>
                  <Button
                    label="New"
                    icon="pi pi-plus"
                    severity="success"
                    iconPos="right"
                    class="h-10"
                    @click="displayNewSecurityTemplateModal"
                  ></Button>
                  <Button
                    label="Copy"
                    icon="pi pi-copy"
                    iconPos="right"
                    class="h-10"
                    severity="info"
                    :disabled="!securityGroup"
                    @click="copySecurityGroupPermissions"
                  ></Button>
                </div>
              </div>
              <div class="flex flex-col gap-2">
                <FormRadioButtonGroup name="userRole" :options="Object.values(UserRole)" label="User Role:"></FormRadioButtonGroup>
                <Tabs value="0" class="border-surface-100 border-1 border-t-0 dark:border-surface-900 rounded-b-lg shadow">
                  <TabList>
                    <Tab value="0">Application</Tab>
                    <Tab value="1">Projects</Tab>
                    <Tab value="2">Entities</Tab>
                    <Tab value="3">Ledgers</Tab>
                    <Tab value="4">Draws</Tab>
                  </TabList>
                  <TabPanels>
                    <TabPanel value="0">
                      <div class="flex flex-col gap-2">
                        <LabeledCheckbox name="permissions.printReports" label="Print Reports" switch />
                        <!-- <LabeledCheckbox name="permissions.manageUserGroups" label="Manage user groups (Add/Delete/Rename)" switch /> -->
                        <LabeledCheckbox name="permissions.managePropertyTypes" label="Manage property types" switch />
                      </div>
                    </TabPanel>
                    <TabPanel value="1">
                      <div class="grid grid-cols-1 lg:grid-cols-2 gap-2">
                        <LabeledCheckbox name="permissions.addProject" label="Add Project" switch />
                        <LabeledCheckbox name="permissions.editProject" label="Edit Project" switch />
                        <LabeledCheckbox name="permissions.deleteProject" label="Delete Project" switch />
                        <LabeledCheckbox name="permissions.archiveProject" label="Archive Project" switch />
                        <LabeledCheckbox name="permissions.restoreProject" label="Restore Project" switch />
                        <div class="mt-2 col-span-2"></div>
                        <LabeledCheckbox
                          name="permissions.modifyAfterDrawZero"
                          label="Modify Project Entities After Draw 0"
                          switch
                          size="col-span-2"
                        />
                        <!-- <LabeledCheckbox
                        name="permissions.hideFinancialDataProject"
                        label="Edit Hide Financial Data From Borrowers"
                        switch
                        size="col-span-2"
                      /> -->
                      </div>
                    </TabPanel>
                    <TabPanel value="2">
                      <div class="flex flex-col gap-2">
                        <LabeledCheckbox name="permissions.addEntity" label="Add Entity" switch />
                        <LabeledCheckbox name="permissions.editEntity" label="Edit Entity" switch />
                        <LabeledCheckbox name="permissions.deleteEntity" label="Delete Entity" switch />
                      </div>
                    </TabPanel>
                    <TabPanel value="3">
                      <div class="grid grid-cols-1 lg:grid-cols-2 gap-2">
                        <LabeledCheckbox name="permissions.addLedger" label="Add Ledger" switch />
                        <LabeledCheckbox name="permissions.editLedger" label="Edit Ledger" switch />
                        <!-- <LabeledCheckbox name="permissions.deleteLedger" label="Delete Ledger" switch /> -->
                        <div class="col-span-2 mt-2"></div>
                        <LabeledCheckbox name="permissions.voidTransaction" label="Void Transactions" switch />
                        <LabeledCheckbox name="permissions.processSelectedTasks" label="Process Selected Tasks" switch />
                        <LabeledCheckbox name="permissions.reconcileProjects" label="Reconcile Projects" switch />
                        <div class="col-span-2 mt-2"></div>
                        <LabeledCheckbox name="permissions.manualImport" label="Manual Import" switch />
                        <LabeledCheckbox name="permissions.manualExport" label="Manual Export" switch />
                      </div>
                    </TabPanel>
                    <TabPanel value="4">
                      <div class="grid grid-cols-1 lg:grid-cols-2 gap-2">
                        <LabeledCheckbox name="permissions.addDraw" label="Add Draw" switch />
                        <LabeledCheckbox name="permissions.completeDraw" label="Complete Draw" switch />
                        <!-- <LabeledCheckbox name="permissions.rollbackDraw" label="Rollback Draw" switch /> -->
                        <!-- <LabeledCheckbox name="permissions.fundDraw" label="Fund Draw" switch /> -->
                        <div class="col-span-2 mt-2"></div>
                        <LabeledCheckbox
                          name="permissions.approveAll"
                          label="Approve All"
                          switch
                          size="col-span-2"
                          @change="toggleDrawApproval(DrawApprovalOption.ALL)"
                        />
                        <LabeledCheckbox
                          name="permissions.approveWithActualVariance"
                          label="Approve w/ Actual Variance"
                          switch
                          size="col-span-2"
                          @change="toggleDrawApproval(DrawApprovalOption.ACTUAL)"
                        />
                        <LabeledCheckbox
                          name="permissions.approveWithRedNetEstVariance"
                          label="Approve w/ Red Net Estimated Variance"
                          switch
                          size="col-span-2"
                          @change="toggleDrawApproval(DrawApprovalOption.EST_VARIANCE)"
                        />
                        <LabeledCheckbox
                          name="permissions.approveWithRedNetEstVarianceAmt"
                          label="Approve w/ Red Net Estimated Variance Under"
                          switch
                          size="col-span-2"
                          @change="toggleDrawApproval(DrawApprovalOption.EST_VAIRANCE_UNDER)"
                        />
                        <div class="col-span-2 mt-2"></div>
                        <LabeledCheckbox name="permissions.approveLienWaivers" label="Approve Lien Waivers" switch />
                        <!-- <LabeledCheckbox name="permissions.voidTransactionDraw" label="Void A Transaction" switch /> -->
                        <LabeledCheckbox name="permissions.approveClean" label="Approve Clean" switch />
                        <LabeledCheckbox name="permissions.addLienWaiverDescriptions" label="Add Lien Waiver Descriptions" switch />
                        <!-- <LabeledCheckbox name="permissions.processSelectedTasksDraw" label="Process Selected Tasks" switch /> -->
                      </div>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </div>
            </Fieldset>
          </div>
          <div class="mt-2 w-full flex justify-end">
            <SubmitButton />
          </div>
        </div>
      </form>
    </template>
  </Card>
  <TSDialog label="Add Security Group" v-model:visible="addSecurityGroup" icon="plus" size="w-[500px]!">
    <template #content>
      <div class="flex flex-col gap-2">
        <label class="font-bold">Name:</label>
        <InputText v-model="newSecurityGroupName" />
        <Button label="Add" icon="pi pi-check" iconPos="right" @click="newSecurityTemplate"></Button>
        <Button label="Cancel" icon="pi pi-times" iconPos="right" severity="surface" @click="addSecurityGroup = false"></Button>
      </div>
    </template>
  </TSDialog>
</template>

<script setup lang="ts">
import { useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import { z } from "zod";
import { UserPermissionsSchema, type UserDetail } from "@/types/User";
import { getUserGroups } from "@/services/Utility";
import { CodeSchema, type Code } from "@/types/Codes";
import { UserRole } from "@/enums/UserRole";
import { SecurityGroupListSchema, SecurityGroupSchema, type SecurityGroup } from "@/types/SecurityGroup";
import { useConfirm } from "primevue/useconfirm";

const messages = ref<string[]>([]);
const userGroups = ref<Code[]>([]);
const securityGroups = ref<SecurityGroup[]>([]);
const securityGroup = ref<SecurityGroup>();
const newSecurityGroupName = ref("");
const addSecurityGroup = ref(false);

const emit = defineEmits<{
  submitted: [];
}>();

interface Props {
  user?: UserDetail;
}
const props = defineProps<Props>();

const enum DrawApprovalOption {
  ALL = 0,
  ACTUAL = 1,
  EST_VARIANCE = 2,
  EST_VAIRANCE_UNDER = 3,
}

const initialValues: any = {};

if (props.user) {
  initialValues.firstName = props.user.firstName;
  initialValues.lastName = props.user.lastName;
  initialValues.userName = props.user.userName;
  initialValues.userGroup = props.user.userGroup;
  initialValues.permissions = props.user.permissions;
  initialValues.email = props.user.email;
  initialValues.active = props.user.active;

  if (props.user.administrator) {
    initialValues.userRole = UserRole.ADMINISTRATOR;
  } else if (props.user.drawAssistant) {
    initialValues.userRole = UserRole.DRAW_ASSISTANT;
  } else {
    initialValues.userRole = UserRole.USER;
  }
} else {
  initialValues.userRole = UserRole.USER;
  initialValues.active = false;

  initialValues.permissions = {
    printReports: true,
    addProject: true,
    editProject: true,
    deleteProject: true,
    archiveProject: true,
    restoreProject: true,
    addEntity: true,
    editEntity: true,
    deleteEntity: true,
    addLedger: true,
    editLedger: true,
    deleteLedger: true,
    voidTransaction: true,
    processSelectedTasks: true,
    manualImport: true,
    manualExport: true,
    addDraw: true,
    voidTransactionDraw: true,
    processSelectedTasksDraw: true,
    approveAll: true,
    rollbackDraw: true,
  };
}

const { handleSubmit, setFieldValue, values } = useForm({
  validationSchema: toTypedSchema(
    z.object({
      firstName: z.string().min(1, { message: "First Name is required" }),
      lastName: z.string().min(1, { message: "Last Name is required" }),
      userName: z.string().min(1, { message: "Username is required" }),
      userGroup: CodeSchema.nullable(),
      userRole: z.string(),
      permissions: UserPermissionsSchema,
      email: z.string().min(1, { message: "E-mail is required" }).email({ message: "Please enter a valid e-mail" }),
      active: z.boolean(),
    })
  ),
  initialValues: initialValues,
  keepValuesOnUnmount: true,
});

const copySecurityGroupPermissions = (): void => {
  if (securityGroup.value) {
    setFieldValue("permissions", securityGroup.value.permissions);
    if (securityGroup.value.permissions.administrator) setFieldValue("userRole", UserRole.ADMINISTRATOR);
    else if (securityGroup.value.permissions.drawAssistant) setFieldValue("userRole", UserRole.DRAW_ASSISTANT);
    else setFieldValue("userRole", UserRole.USER);
  }
};

const toggleDrawApproval = (option: DrawApprovalOption): void => {
  if (option === DrawApprovalOption.ALL && values.permissions.approveAll) {
    setFieldValue("permissions.approveWithActualVariance", false);
    setFieldValue("permissions.approveWithRedNetEstVariance", false);
    setFieldValue("permissions.approveWithRedNetEstVarianceAmt", false);
  } else if (option === DrawApprovalOption.ACTUAL && values.permissions.approveWithActualVariance) {
    setFieldValue("permissions.approveAll", false);
    setFieldValue("permissions.approveWithRedNetEstVariance", false);
    setFieldValue("permissions.approveWithRedNetEstVarianceAmt", false);
  } else if (option === DrawApprovalOption.EST_VARIANCE && values.permissions.approveWithRedNetEstVariance) {
    setFieldValue("permissions.approveAll", false);
    setFieldValue("permissions.approveWithActualVariance", false);
    setFieldValue("permissions.approveWithRedNetEstVarianceAmt", false);
  } else if (option === DrawApprovalOption.EST_VAIRANCE_UNDER && values.permissions.approveWithRedNetEstVarianceAmt) {
    setFieldValue("permissions.approveAll", false);
    setFieldValue("permissions.approveWithActualVariance", false);
    setFieldValue("permissions.approveWithRedNetEstVariance", false);
  }
};

const confirm = useConfirm();
const onSubmit = handleSubmit((values: any): void => {
  if (props.user == null) {
    confirmPopup(
      confirm,
      "Activate User?",
      "Do you want to activate this user?",
      () => {
        values.active = true;
        sendRequest(values);
      },
      () => sendRequest(values)
    );
  } else {
    sendRequest(values);
  }
});

const sendRequest = (values: any) => {
  let url = props.user ? "/users/edit/" + props.user.userId : "/users/add";

  axios
    .post(url, values)
    .then((response) =>
      handleApiResponse(
        response,
        () => {
          emit("submitted");
          success("Success", props.user ? "User details edited" : "New user added");
        },
        (errors: ApiError[]) => {
          // Clear existing errors
          while (messages.value.length) {
            messages.value.pop();
          }

          messages.value.push("Please fix the following errors:");

          for (let error of errors) {
            messages.value.push(error.message);
          }
        }
      )
    )
    .catch((e) => errorApi(e));
};

const saveSecurityGroupPermissions = (): void => {
  if (securityGroup.value == null) return;

  securityGroup.value.userRole = values.userRole;

  axios
    .post("/security-groups/update", securityGroup.value)
    .then((response) =>
      handleApiResponse(
        response,
        () => {
          success("Security Group Updated", "");
        },
        (errors: ApiError[]) => {
          if (errors.length > 0) error("Error occurred updating security groups", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));
};

const deleteSecurityGroupPermissions = (): void => {
  if (securityGroup.value == null) return;
  axios
    .post("/security-groups/delete/" + securityGroup.value.id)
    .then((response) =>
      handleApiResponse(
        response,
        () => {
          if (securityGroup.value == null) return;
          const index = securityGroups.value.indexOf(securityGroup.value);
          securityGroups.value.splice(index, 1);
          securityGroup.value = undefined;
          success("Security Group Deleted", "");
        },
        (errors: ApiError[]) => {
          if (errors.length > 0) error("Error occurred deleting security groups", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));
};

const displayNewSecurityTemplateModal = (): void => {
  addSecurityGroup.value = true;
  newSecurityGroupName.value = "";
};

const newSecurityTemplate = (): void => {
  if (newSecurityGroupName.value === "") {
    error("Invalid Security Group", "Name may not be blank");
    return;
  }

  const newSecurityGroup: SecurityGroup = {
    id: 0,
    name: newSecurityGroupName.value,
    permissions: values.permissions,
    userRole: values.userRole,
  };

  axios
    .post("/security-groups/add", newSecurityGroup)
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          parseData(SecurityGroupSchema, data, securityGroup);
          if (securityGroup.value) {
            securityGroups.value.push(securityGroup.value);
            addSecurityGroup.value = false;
            success("Security Group Added", "");
          }
        },
        (errors: ApiError[]) => {
          if (errors.length > 0) error("Error occurred adding security groups", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));
};

onBeforeMount(async (): Promise<void> => {
  await getUserGroups(false, false).then((result: Code[]) => (userGroups.value = result));
  await axios
    .get("/security-groups")
    .then((response) =>
      handleApiResponse(
        response,
        (data: any) => {
          parseData(SecurityGroupListSchema, data, securityGroups);
        },
        (errors: ApiError[]) => {
          if (errors.length > 0) error("Error occurred getting security groups", errors[0].message);
        }
      )
    )
    .catch((e) => errorApi(e));
});
</script>
